import React, { useState } from 'react';
import { publishedPosts, getReadingTime, type BlogPost } from '../content/posts';
import { pickHeroMoment } from '../content/heroMoments';
import { selectEvergreenCoverline } from '../utils/homeSelection';
import { useTodaysCall } from '../hooks/useTodaysCall';
import { compareLiveGames, useLiveSports, type UseLiveSportsReturn } from '../hooks/useLiveSports';
import { appConfig } from '../config';
import { siteConfig, type SocialLink } from '../config/site';
import SiteHeader from '../components/SiteHeader';
import { useSeo } from '../hooks/useSeo';
import type { Theme, LiveGame } from '../types/index.ts';

/** "Frankie & Sloane" / "A, B & C" — Oxford-free list join for prose. */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

const SOCIAL_LABELS: Record<SocialLink['platform'], string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  x: 'X',
};

interface LandingPageProps {
  theme: Theme;
  onToggleTheme: () => void;
}

function formatBoardTime(kickoff: string): string {
  const date = new Date(kickoff);
  if (Number.isNaN(date.getTime())) return 'Time TBD';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function pickBoardGame(liveSports: UseLiveSportsReturn): LiveGame | null {
  // Feature something people can still act on. freshnessRank sorts same-day
  // games first — including finals from earlier today — so drop finished games
  // before picking, or "one game worth your time" ends up already over.
  return [...liveSports.leagues.flatMap((league) => league.games)]
    .filter((game) => game.status !== 'final')
    .sort(compareLiveGames)[0] ?? null;
}

function FeaturedParlay({ liveSports, predictionsOn }: { liveSports: UseLiveSportsReturn; predictionsOn: boolean }) {
  const game = pickBoardGame(liveSports);
  const call = useTodaysCall(predictionsOn);
  const recommendationHref = predictionsOn ? '/predictions' : '/blog';
  const recommendationLabel = predictionsOn ? 'Read the pick' : 'Read the latest';

  // The posted pick and the scoreboard's next game are independent feeds and are
  // usually different games (and leagues). Commit to ONE source per render so we
  // never pair one game's matchup with another game's league/kickoff.
  const featured = call.call
    ? {
        meta: call.call.leagueLabel,
        matchup: call.call.matchup,
        headline: call.call.headline,
        oddsLabel: call.call.oddsLabel,
      }
    : game
      ? {
          meta: `${game.league} · ${formatBoardTime(game.kickoff)}`,
          matchup: `${game.awayTeam.name} @ ${game.homeTeam.name}`,
          headline: 'The big-game card is on deck.',
          oddsLabel: null as string | null,
        }
      : null;

  return (
    <aside className="featured-parlay pr pr-3" aria-label="Featured game recommendation">
      <p className="section-kicker">one game worth your time</p>
      <span className="featured-parlay__rule" aria-hidden="true" />
      {featured ? (
        <>
          <p className="featured-parlay__meta">{featured.meta}</p>
          <h2>{featured.matchup}</h2>
          <p className="featured-parlay__pick">{featured.headline}</p>
          {featured.oddsLabel && <b className="featured-parlay__odds">{featured.oddsLabel}</b>}
          <a href={recommendationHref} className="featured-parlay__link">{recommendationLabel} <span aria-hidden="true">→</span></a>
        </>
      ) : (
        <>
          <h2>No forced pick.</h2>
          <p className="featured-parlay__pick">When there is a game worth building around, it lands here. Until then, the blog has the good stuff.</p>
          <a href="/blog" className="featured-parlay__link">Read the latest <span aria-hidden="true">→</span></a>
        </>
      )}
    </aside>
  );
}

const LandingPage: React.FC<LandingPageProps> = ({ theme, onToggleTheme }) => {
  // Landing uses the site-default title (no page suffix) and the root canonical.
  useSeo({ path: '/' });

  const [hero] = useState(pickHeroMoment);
  const predictionsOn = Boolean(appConfig.features?.enablePredictions);
  const liveSports = useLiveSports();

  // Keep the front page intentionally short: the newest post and one useful
  // evergreen piece, never a grid of competing destinations.
  const published = publishedPosts();
  const newest: BlogPost | undefined = published[0];
  const evergreen = selectEvergreenCoverline(published, [newest?.slug].filter((slug): slug is string => Boolean(slug)));
  const highlights = [newest, evergreen].filter((post): post is BlogPost => Boolean(post));

  const handleFrontpagePointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    event.currentTarget.style.setProperty('--fp-move-x', x.toFixed(3));
    event.currentTarget.style.setProperty('--fp-move-y', y.toFixed(3));
  };

  const handleFrontpagePointerLeave = (event: React.PointerEvent<HTMLElement>) => {
    event.currentTarget.style.setProperty('--fp-move-x', '0');
    event.currentTarget.style.setProperty('--fp-move-y', '0');
  };

  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:bg-[var(--fp-magenta)] focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-[var(--fp-paper)]">
        Skip to content
      </a>

      <main
        className="frontpage"
        id="main-content"
        onPointerMove={handleFrontpagePointerMove}
        onPointerLeave={handleFrontpagePointerLeave}
      >
        <div className="fp-texture" aria-hidden="true" />

        <SiteHeader theme={theme} onToggleTheme={onToggleTheme} liveSports={liveSports} />

        <div className="fp-shell">
          <div className="fp-top">
          <section className="splash">
            <h1 className="splash__headline pr pr-1">
              {hero.lead} <span className="mark">{hero.mark}</span>
            </h1>
            <p className="splash__dek pr pr-2">{hero.dek}</p>
          </section>
          <FeaturedParlay liveSports={liveSports} predictionsOn={predictionsOn} />
          </div>

        <section className="reads reads--highlights" aria-label="Article highlights">
          <div className="reads__header">
            <div>
              <p className="section-kicker">from the desk</p>
              <h2>Two reads. Start here.</h2>
            </div>
            <a className="text-link" href="/blog">View all</a>
          </div>

          <div className="reads__grid">
            {highlights.map((post, index) => (
              <a className="read-card" href={`/blog/${post.slug}`} key={post.slug}>
                <span>{index === 0 ? 'Fresh ink' : post.category}</span>
                <h3>{post.title}</h3>
                <p>{post.excerpt}</p>
                <small>{getReadingTime(post)}</small>
              </a>
            ))}
          </div>
        </section>

        <footer className="colophon">
          <p className="colophon__byline">
            Founded &amp; edited by <b>{joinNames(siteConfig.founders)}</b>
          </p>

          <nav className="colophon__socials" aria-label="Social links">
            {siteConfig.socials.map((social) => {
              const label = SOCIAL_LABELS[social.platform];
              const Icon = SOCIAL_ICONS[social.platform];
              return social.url ? (
                <a
                  key={social.platform}
                  className="social-link"
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${label}${social.handle ? ` (${social.handle})` : ''}`}
                >
                  <Icon />
                </a>
              ) : (
                <span
                  key={social.platform}
                  className="social-link social-link--disabled"
                  aria-label={`${label} coming soon`}
                  aria-disabled="true"
                >
                  <Icon />
                </span>
              );
            })}
          </nav>

          <div className="colophon__legal">
            <span className="barcode" aria-hidden="true">
              {Array.from({ length: 34 }).map((_, i) => <i key={i} />)}
              <span className="barcode__num">0 01 · 2026</span>
            </span>
            <span className="gamble">21+ · if it stops being fun, stop · 1-800-gambler</span>
            <span>© 2026 2 Girls 1 Parlay</span>
          </div>
        </footer>
        </div>
      </main>
    </>
  );
};

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="3.5" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.2 1.6 3.8 3.7 4.1v2.6c-1.3.1-2.5-.3-3.7-1v5.9c0 3.6-2.6 6-5.8 6A5.7 5.7 0 0 1 5 14.9c0-3.4 3-5.9 6.4-5.2v2.7c-.4-.1-.9-.2-1.3-.2-1.5 0-2.7 1.1-2.7 2.6 0 1.6 1.2 2.7 2.7 2.7 1.6 0 2.8-1.2 2.8-3.1V3h1.6z" />
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.5 3h3l-6.6 7.6L21.8 21h-5.9l-4.3-5.6L6.6 21H3.5l7-8L2.5 3h6l3.9 5.1L17.5 3zm-1 16h1.6L8.1 4.7H6.4l10.1 14.3z" />
  </svg>
);

const SOCIAL_ICONS: Record<SocialLink['platform'], React.FC> = {
  instagram: InstagramIcon,
  tiktok: TikTokIcon,
  x: XIcon,
};

export default LandingPage;
